import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum';
import { RankingActionEnum } from '@empathybroker/tsclient-play/dist/enums/ranking-action.enum';
import { Facet } from '@empathybroker/tsclient-play/dist/models/facet';
import { FacetValue } from '@empathybroker/tsclient-play/dist/models/facet-value';
import { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config';
import { RankingElement } from '@empathybroker/tsclient-play/dist/models/ranking-element';
import { SearchError, SearchResponse } from '@empathybroker/tsclient-search';

import Vue from 'vue';
import { mapGetters } from 'vuex';

import { ApiEnum, PermissionLevelEnum, SearchDataLoadTypeEnum } from '../enums';

import { SetContextWithUrlGuard } from '../guards';

import { CrudFormMixin } from '../mixins/crud-form.mixin';

import { FeatureNode, Pagination, RegularFilter, RegularFilterSelectedValues } from '../models';

import { APP_CONSTS } from '../store/app/app.consts';

export const ConfigurationsFormMixin = {
  mixins: [CrudFormMixin],
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree
    }),
    valid: function (): boolean {
      return (
        this.loadSearchDataEnabled && (
          (!!this.item.ranking.products && !!this.item.ranking.products.elements.length) ||
          (!!this.item.ranking.attributes && !!this.item.ranking.attributes.elements.length) ||
          (!!this.item.facets && !!this.item.facets.elements.length)
        )
      );
    },
    searchFilters: function (): RegularFilter[] {
      return Object.values(this.pivots)
        .map((filter: RegularFilter) => ({
          ...filter,
          config: {
            ...filter.config,
            values: this.getSearchFilterValueConfigs(filter)
          }
        }))
        .concat(Object.values(this.filters))
        .map((filter: RegularFilter) => ({
          ...filter,
          config: {
            ...filter.config,
            mandatory: true,
            multiselectDisabled: true
          },
          selectedValues: this.getSearchFilterSelectedValues(filter)
        }));
    },
    visibleSearchFilters: function (): RegularFilter[] {
      return this.searchFilters.filter((filter: RegularFilter) => (filter.config.values.length > 1));
    },
    loadSearchDataEnabled: function (): boolean {
      return Object.values(this.pivots).every((pivot: RegularFilter) => !!pivot.selectedValues.length);
    },
    toolsAvailability: function () {
      const toolsAvailability = {};
      const domainFeatureNodes = this.featuresTree[this.context.domain];
      if (!!domainFeatureNodes) {
        const configurationsFeatureNode = SetContextWithUrlGuard.getFeatureNode(this.dataType, domainFeatureNodes);
        configurationsFeatureNode.children.forEach((featureNode: FeatureNode) => {
          if (featureNode.permissionLevel === PermissionLevelEnum.WRITE) {
            toolsAvailability[featureNode.id] = true;
          }
        });
      }
      return toolsAvailability;
    },
    resultsPreviewParams: function () {
      const params: any = {
        productsRankingField: this.instanceConfig.catalog.resultMappings.id
      };
      if (!!this.item.ranking.products) {
        const boostedProductIds: string[] = [];
        const buriedProductIds: string[] = [];
        this.item.ranking.products.elements.forEach((element: RankingElement) => {
          const productId = element.fieldValue;
          return {
            [RankingActionEnum.BOOST]: () => boostedProductIds.push(productId),
            [RankingActionEnum.BURY]: () => buriedProductIds.push(productId)
          }[element.action]();
        });
        params.productsRankingBoostsIds = boostedProductIds.join();
        params.productsRankingBuriesIds = buriedProductIds.join();
      }
      if (!!this.item.ranking.attributes) {
        const boostedAttributes: string[] = [];
        const buriedAttributes: string[] = [];
        this.item.ranking.attributes.elements.forEach((element: RankingElement) => {
          const attributeRankingParam = `${ element.fieldName }:${ element.fieldValue }`;
          return {
            [RankingActionEnum.BOOST]: () => boostedAttributes.push(attributeRankingParam),
            [RankingActionEnum.BURY]: () => buriedAttributes.push(attributeRankingParam)
          }[element.action]();
        });
        params.attributesRankingBoosts = boostedAttributes.join();
        params.attributesRankingBuries = buriedAttributes.join();
      }
      return params;
    },
    facetsPreviewParams: function () {
      if (!!this.item.facets) {
        return this.item.facets.elements.reduce((params, facet: Facet) => {
          params.facet.push(facet.id);
          params[`f.${ facet.id }.sort`] = (facet.sortType || '');
          Object.values(RankingActionEnum).forEach((action: RankingActionEnum) => {
            params[this.getFacetActionParamName(facet.id, action)] = '';
          });
          if (!!facet.values) {
            facet.values.forEach((facetValue: FacetValue) => {
              const facetActionParamName = this.getFacetActionParamName(facet.id, facetValue.action);
              if (!params[facetActionParamName]) {
                params[facetActionParamName] = facetValue.id;
              } else {
                params[facetActionParamName] += `,${ facetValue.id }`;
              }
            });
          }
          return params;
        }, { facet: [] });
      }
    },
    loadPreviewDisabled: function (): boolean {
      return false;
    }
  },
  data: function () {
    return {
      dataType: ActionTypeEnum.CONFIGURATION,
      item: {
        enabled: true,
        name: undefined,
        context: {
          pivots: {}
        },
        ranking: {
          products: {
            elements: []
          },
          attributes: {
            elements: []
          }
        },
        facets: {
          elements: []
        }
      },
      searchData: {
        results: [],
        facets: [],
        loading: {
          [SearchDataLoadTypeEnum.RESULTS]: false,
          [SearchDataLoadTypeEnum.FACETS]: false
        },
        error: undefined
      },
      searchFiltersSelectedValues: {},
      pagination: {
        page: 1,
        itemsPerPage: 20,
        totalItems: 0
      },
      watchers: {},
      messages: this.$epb.messages.tools.configurations
    };
  },
  methods: {
    onItemLoad: async function (): Promise<void> {
      await CrudFormMixin.methods.onItemLoad.bind(this)();
      this.watchers.pivots = this.$watch('item.context.pivots', this.onContextPivotsWatch, { deep: true });
      this.watchers.searchFilters = this.$watch('searchFilters', this.onSearchFiltersWatch, { deep: true });
      this.watchers.resultsPreview = this.$watch('resultsPreviewParams', this.onResultsPreviewParamsWatch, { deep: true });
      this.watchers.facetsPreview = this.$watch('facetsPreviewParams', this.onFacetsPreviewParamsWatch, { deep: true });
      await this.loadSearchData({ resetPagination: true });
    },
    loadSearchData: async function ({ loadType, resetPagination }): Promise<void> {
      if (this.loadSearchDataEnabled) {
        if (resetPagination) {
          this.pagination.page = 1;
        }
        this.setSearchDataLoading(loadType, true);
        try {
          const searchResponse = await this.fetchSearchData();
          this.searchData.results = searchResponse.data.items;
          this.pagination.totalItems = searchResponse.data.total;
          this.searchData.facets = searchResponse.data.facets; // TODO don't override facets if user query is set
        } catch (error) {
          this.$emit('showError', this.$epb.utils.decodeErrorMessage(error, ApiEnum.SEARCH));
          this.searchData.results = [];
          this.pagination.totalItems = 0;
          this.searchData.facets = [];
        } finally {
          this.setSearchDataLoading(loadType, false);
        }
      }
    },
    setSearchDataLoading: function (loadType: SearchDataLoadTypeEnum, loading: boolean): void {
      if (!!loadType) {
        this.searchData.loading[loadType] = loading;
      } else {
        Object.keys(this.searchData.loading).forEach((searchDataLoadType: SearchDataLoadTypeEnum) => {
          this.searchData.loading[searchDataLoadType] = loading;
        });
      }
    },
    fetchSearchData: async function (): Promise<SearchResponse | SearchError> {
      return Promise.reject({ error: 400 });
    },
    getSearchFilterValueConfigs: function (filter: RegularFilter): RegularFilterValueConfig[] {
      return filter.config.values.filter(
        (filterValueConfig: RegularFilterValueConfig) => (filter.selectedValues.includes(filterValueConfig.id)));
    },
    getSearchFilterSelectedValues: function (filter: RegularFilter): RegularFilterSelectedValues {
      let filterSelectedValues = (this.searchFiltersSelectedValues[filter.config.id] || []);
      if (!filterSelectedValues.length && !!filter.config.defaultValueId) {
        filterSelectedValues = [filter.config.defaultValueId];
      }
      filterSelectedValues = filterSelectedValues.filter(
        (filterSelectedValue: string) => filter.config.values.some((filterValueConfig: RegularFilterValueConfig) =>
          (filterValueConfig.id === filterSelectedValue)));
      if (!filterSelectedValues.length && !!filter.config.values.length) {
        filterSelectedValues = [filter.config.values[0].id];
      }
      return filterSelectedValues;
    },
    onSearchFilterSelectedValuesChange: function (filter: RegularFilter, selectedValues: RegularFilterSelectedValues): void {
      Vue.set(this.searchFiltersSelectedValues, filter.config.id, selectedValues);
    },
    onPaginationChange: function (pagination: Pagination): void {
      this.pagination = pagination;
      this.loadSearchData({ loadType: SearchDataLoadTypeEnum.RESULTS });
    },
    getFacetActionParamName: function (facetId: string, action: RankingActionEnum): string {
      const actionParamName = {
        [RankingActionEnum.BOOST]: 'boosts',
        [RankingActionEnum.BURY]: 'buries'
      }[action];
      return `f.${ facetId }.${ actionParamName }`;
    },
    onContextPivotsWatch: function (): void {
      Object.entries(this.pivots).forEach(([filterId, filter]: [string, RegularFilter]) => {
        if (!!this.searchFiltersSelectedValues[filterId] &&
          !filter.selectedValues.some((pivotValue: string) => this.searchFiltersSelectedValues[filterId].includes(pivotValue))) {
          this.searchFiltersSelectedValues[filterId] = [];
        }
      });
    },
    onSearchFiltersWatch: async function (): Promise<void> {
      await this.loadSearchData({ resetPagination: true });
    },
    onResultsPreviewParamsWatch: async function (): Promise<void> {
      if (!this.loadPreviewDisabled) {
        await this.loadSearchData({ loadType: SearchDataLoadTypeEnum.RESULTS });
      }
    },
    onFacetsPreviewParamsWatch: async function (): Promise<void> {
      if (!this.loadPreviewDisabled) {
        await this.loadSearchData({ loadType: SearchDataLoadTypeEnum.FACETS });
      }
    }
  },
  beforeDestroy: function (): void {
    Object.values(this.watchers).forEach((watcher: Function) => watcher());
  }
};
